import { usePathname } from "next/navigation";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

let GLOBAL_IS_FULL_SCREEN = false;

export function useDeviceNavigation({
  closeOnBack,
  closeOnNavigation,
  onArrowKey,
}: {
  closeOnBack?: boolean;
  closeOnNavigation?: boolean;
  onArrowKey?: (delta: number) => void;
} = {}): {
  isFullScreen: boolean;
  setIsFullScreen: (isFullScreen: boolean) => void;
} {
  const [isFullScreen, setIsFullScreenState] = useState(
    !closeOnNavigation ? GLOBAL_IS_FULL_SCREEN : false,
  );
  const pathname = usePathname();
  const pastPathName = useRef<string>(pathname);

  const setIsFullScreen = useCallback(
    (isFullScreen: boolean) => {
      if (!closeOnNavigation) {
        GLOBAL_IS_FULL_SCREEN = isFullScreen;
      }
      setIsFullScreenState(isFullScreen);
    },
    [closeOnNavigation],
  );

  useEffect(() => {
    if (
      closeOnNavigation &&
      isFullScreen &&
      pastPathName.current !== pathname
    ) {
      setIsFullScreen(false);
    }
    pastPathName.current = pathname;
  }, [pathname, isFullScreen, setIsFullScreen, closeOnNavigation]);

  const setIsFullScreenWithHistory = useCallback(
    (isFullScreenUpdate: boolean) => {
      if (isFullScreen === isFullScreenUpdate) {
        return;
      }
      setIsFullScreen(isFullScreenUpdate);
      if (isFullScreenUpdate) {
        window.history.pushState(null, "");
      } else {
        window.history.back();
      }
    },
    [isFullScreen, setIsFullScreen],
  );

  useLayoutEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsFullScreenWithHistory(false);
      }
      if (event.key === "ArrowRight") {
        onArrowKey?.(1);
      }
      if (event.key === "ArrowLeft") {
        onArrowKey?.(-1);
      }
    };
    const handleBackButton = () => {
      setIsFullScreen(false);
    };
    // Prevent scrolling on mount
    document.body.style.overflow = isFullScreen ? "hidden" : "";

    if (closeOnBack) {
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("popstate", handleBackButton);
    }

    // Re-enable scrolling on unmount
    return () => {
      document.body.style.overflow = "";

      if (closeOnBack) {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("popstate", handleBackButton);
      }
    };
  }, [
    closeOnBack,
    isFullScreen,
    setIsFullScreen,
    setIsFullScreenWithHistory,
    onArrowKey,
  ]);

  return {
    isFullScreen,
    setIsFullScreen: closeOnBack ? setIsFullScreenWithHistory : setIsFullScreen,
  };
}
